import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, MapPin, ExternalLink, Bookmark, Sparkles, Loader2, Building2, ChevronRight, Lightbulb } from "lucide-react";
import CompanyLogo from "@/components/CompanyLogo";
import DOMPurify from "dompurify";

// Sanitise and clean job description HTML for legible rendering.
// Strips scripts, event handlers, inline styles, MS-Word data-* attributes.
// Also decodes HTML entities (Arbeitnow returns double-escaped `&lt;div&gt;` style).
function decodeEntities(str) {
  if (!str) return "";
  // Runs entities through the browser's parser twice for double-escaped content.
  const ta = document.createElement("textarea");
  ta.innerHTML = str;
  let out = ta.value;
  if (out.includes("&lt;") || out.includes("&amp;")) {
    ta.innerHTML = out;
    out = ta.value;
  }
  return out;
}

function cleanDescriptionHtml(raw) {
  if (!raw) return "";
  let html = decodeEntities(String(raw));
  // Remove Word/Office data-* attributes that mangle layout
  html = html.replace(/\sdata-[a-z0-9-]+="[^"]*"/gi, "");
  // Remove inline styles that break our typography
  html = html.replace(/\sstyle="[^"]*"/gi, "");
  // Remove class attributes (mostly Word-generated)
  html = html.replace(/\sclass="[^"]*"/gi, "");
  // Convert &nbsp; runs to spaces so text wraps nicely
  html = html.replace(/(&nbsp;){2,}/gi, " ");
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p","br","hr","strong","b","em","i","u","s","ul","ol","li","a","blockquote",
      "code","pre","h1","h2","h3","h4","h5","h6","span","div","table","thead","tbody","tr","th","td",
    ],
    ALLOWED_ATTR: ["href","title","target","rel"],
    ALLOW_DATA_ATTR: false,
  });
  return clean;
}

export default function JobDetail() {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();
  const isPro = user?.plan === "pro";
  const cleanDesc = useMemo(() => cleanDescriptionHtml(job?.description || ""), [job?.description]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/jobs/${slug}`);
        setJob(data);
      } catch (e) {
        toast.error("Job not found");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const save = async (status = "saved") => {
    if (!user) { nav("/login"); return; }
    try {
      await api.post("/applications", {
        job_id: job.id, job_title: job.title, company: job.company, job_url: job.url, status,
      });
      toast.success(status === "applied" ? "Marked as applied!" : "Saved to your tracker");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const runInsights = async () => {
    if (!user) { nav("/login"); return; }
    if (!isPro) { toast.error("Upgrade to Pro for AI Company Insights"); return; }
    setInsightsBusy(true);
    try {
      const clean = job.description.replace(/<[^>]+>/g, " ").slice(0, 6000);
      const { data } = await api.post("/ai/company-insights", {
        company: job.company, job_description: clean, job_title: job.title,
      });
      setInsights(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Insights failed");
    } finally { setInsightsBusy(false); }
  };

  if (loading) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  if (!job) return <div className="p-20 text-center font-bold">Job not found</div>;

  const glassdoor = `https://www.glassdoor.com/Search/results.htm?keyword=${encodeURIComponent(job.company)}`;
  const linkedin = `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(job.company)}`;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
      <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-1 text-sm font-bold mb-6 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to jobs
      </Link>

      <div className="nb-card p-8" data-testid="job-detail">
        <div className="flex items-start gap-4">
          <CompanyLogo company={job.company} size={64} />
          <div className="flex-1">
            <div className="label-eyebrow">{job.company}</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-1">{job.title}</h1>
            <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
              <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{job.location || "Anywhere"}</div>
              {job.remote && <span className="nb-pill bg-[#22c55e] text-white">Remote</span>}
              {job.job_types?.map((t) => <span key={t} className="nb-pill">{t}</span>)}
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {(job.tags || []).map((t) => (<span key={t} className="nb-pill bg-white text-xs">{t}</span>))}
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={async (e) => {
              e.preventDefault();
              const tab = window.open("", "_blank");
              if (tab) { try { tab.opener = null; } catch { /* noop */ } }
              const go = (url) => {
                if (!url) return;
                if (tab && !tab.closed) tab.location.href = url;
                else window.location.href = url;
              };
              try {
                const { data } = await api.get(`/jobs/${job.id}/apply`);
                go(data && data.url ? data.url : job.url);
              } catch {
                go(job.url);
              }
            }}
            data-testid="job-apply-external"
            className="nb-btn bg-[#4338ca] text-white px-5 py-2.5 inline-flex items-center gap-2"
          >
            Apply on company site <ExternalLink className="w-4 h-4" />
          </button>
          <button data-testid="job-save" onClick={() => save("saved")} className="nb-btn bg-white px-5 py-2.5 inline-flex items-center gap-2">
            <Bookmark className="w-4 h-4" /> Save
          </button>
          <button data-testid="job-mark-applied" onClick={() => save("applied")} className="nb-btn bg-[#eab308] px-5 py-2.5">Mark applied</button>
          <Link to="/tailor" state={{ job }} data-testid="job-tailor" className="nb-btn bg-[#ec4899] text-white px-5 py-2.5 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Tailor resume
          </Link>
        </div>
      </div>

      {/* Company Insights section */}
      <div className="nb-card p-8 mt-6" data-testid="company-insights">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg border-2 border-black bg-[#eab308] flex items-center justify-center">
              <Building2 className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="label-eyebrow">Company Insights</div>
              <h2 className="text-2xl font-black tracking-tight">Culture snapshot & research</h2>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={glassdoor} target="_blank" rel="noopener noreferrer" data-testid="link-glassdoor" className="nb-btn bg-[#0caa41] text-white px-4 py-2 text-sm inline-flex items-center gap-1.5">
              Glassdoor <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <a href={linkedin} target="_blank" rel="noopener noreferrer" data-testid="link-linkedin" className="nb-btn bg-[#0a66c2] text-white px-4 py-2 text-sm inline-flex items-center gap-1.5">
              LinkedIn <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {!insights ? (
          <div className="mt-6">
            <p className="text-sm text-gray-700">Generate an AI culture snapshot based on cues in this job description — plus smart questions to ask in the interview.</p>
            <button data-testid="insights-generate" onClick={runInsights} disabled={insightsBusy}
              className="mt-4 nb-btn bg-[#4338ca] text-white px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60">
              {insightsBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Researching…</> : <><Sparkles className="w-4 h-4" /> Generate insights</>}
            </button>
            {!isPro && (
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1"><Lightbulb className="w-3.5 h-3.5" /> Pro users get unlimited AI insights.</p>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-5" data-testid="insights-content">
            <div>
              <div className="label-eyebrow">Culture snapshot</div>
              <p className="mt-2 text-gray-800 leading-relaxed">{insights.culture_snapshot}</p>
            </div>
            {insights.work_style && (
              <div>
                <div className="label-eyebrow">Work style</div>
                <p className="mt-2 text-gray-800 font-semibold">{insights.work_style}</p>
              </div>
            )}
            {insights.values?.length > 0 && (
              <div>
                <div className="label-eyebrow">Signals & values</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {insights.values.map((v, i) => <span key={i} className="nb-pill bg-[#4338ca] text-white text-[11px]">{v}</span>)}
                </div>
              </div>
            )}
            {insights.tech_stack?.length > 0 && (
              <div>
                <div className="label-eyebrow">Tech stack mentioned</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {insights.tech_stack.map((v, i) => <span key={i} className="nb-pill bg-white text-[11px]">{v}</span>)}
                </div>
              </div>
            )}
            {insights.questions_to_ask?.length > 0 && (
              <div>
                <div className="label-eyebrow">Smart questions to ask</div>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {insights.questions_to_ask.map((q, i) => (
                    <li key={i} className="flex gap-2"><ChevronRight className="w-4 h-4 mt-0.5 text-[#ec4899]" strokeWidth={3} />{q}</li>
                  ))}
                </ul>
              </div>
            )}
            {insights.research_prompts?.length > 0 && (
              <div>
                <div className="label-eyebrow">Research checklist</div>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {insights.research_prompts.map((r, i) => (
                    <li key={i} className="flex gap-2"><Lightbulb className="w-4 h-4 mt-0.5 text-[#eab308]" strokeWidth={2.5} />{r}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="pt-4 border-t-2 border-dashed border-black/30 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">Verify these with:</span>
              <a href={glassdoor} target="_blank" rel="noopener noreferrer" className="nb-pill">Glassdoor</a>
              <a href={linkedin} target="_blank" rel="noopener noreferrer" className="nb-pill">LinkedIn</a>
              <button data-testid="insights-regenerate" onClick={runInsights} className="ml-auto nb-pill bg-white">Regenerate</button>
            </div>
          </div>
        )}
      </div>

      <div className="nb-card p-8 mt-6">
        <h2 className="text-xl font-bold mb-3">Full job description</h2>
        <div className="job-description" data-testid="job-description-body" dangerouslySetInnerHTML={{ __html: cleanDesc }} />
      </div>
    </div>
  );
}
